import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import all locale files
import en from '../locales/en.json';
import ja from '../locales/ja.json';
import cn from '../locales/cn.json';

type Locale = 'en' | 'ja' | 'cn';

interface LocalizationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
  getCurrentLanguageCode: () => string;
}

const locales: Record<Locale, any> = {
  en,
  ja,
  cn,
};

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: ReactNode;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>('en');

  // Load saved locale from localStorage on mount
  useEffect(() => {
    const savedLocale = localStorage.getItem('locale') as Locale;
    if (savedLocale && ['en', 'ja', 'cn'].includes(savedLocale)) {
      setLocaleState(savedLocale);
    }
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = locales[locale];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = keys.reduce((obj, k) => obj?.[k], locales.en) || key;
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  const getCurrentLanguageCode = (): string => {
    return t('common.language');
  };

  return (
    <LocalizationContext.Provider value={{ locale, setLocale, t, getCurrentLanguageCode }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}; 